import { apiClient } from './client.js';

export interface AutomationStatus {
  id: string;
  name: string;
  description: string;
  schedule: string;
  scheduleHuman: string;
  service: string;
  category: 'sync' | 'backup' | 'feedback' | 'productivity' | 'marketing' | 'reports' | 'maintenance' | 'external' | 'demo' | 'product';
  /** `stuck`: a run left unfinished for over an hour (the Integrations rule), even if newer runs went through. */
  status: 'healthy' | 'failed' | 'running' | 'stuck' | 'unknown' | 'skipped';
  /** When the live run began, or the oldest stuck one; null when nothing is unfinished. */
  runningSince?: string | null;
  lastRunAt: string | null;
  lastRunState: string | null;
  lastCompletedAt: string | null;
  triggerable: boolean;
  /** A run the OS lost to a restart after the last run that counts. */
  missedAt?: string | null;
  /** The last completed run skipped for want of a connection or key. */
  idleReason?: string | null;
  /** The latest scheduled slot no run that counts began in, and why. Absent on an older API. */
  missedSlot?: { at: string; reason: 'no_run' | 'cancelled' | 'restart' } | null;
  /** Start of a cancelled run newer than `lastRunAt`; the row says "Last: 13 Oct 08:15 · Cancelled". Absent on an older API. */
  lastCancelledAt?: string | null;
  /** The run "Last" shows was started by hand, not by its schedule. Absent on an older API. */
  lastRunManual?: boolean | null;
  /** Switched off in its definition. Absent on an older API. */
  paused?: boolean;
}

export interface AutomationsSummary {
  total: number;
  healthy: number;
  failed: number;
  running: number;
  /** Absent on an API from before stuck runs were counted. */
  stuck?: number;
  unknown: number;
  skipped: number;
}

export interface AutomationsResponse {
  automations: AutomationStatus[];
  summary: AutomationsSummary;
}

export async function getAutomations(): Promise<AutomationsResponse> {
  return apiClient.get('/automations');
}

export interface AutomationHistoryRun {
  state: string;
  createdOn: string;
  startedOn: string | null;
  completedOn: string | null;
  output: unknown;
  retryCount: number;
  error?: string | null;
  /** What started the run. Absent on an older API. */
  trigger?: 'manual' | 'scheduled';
}

export interface AutomationHistoryResponse {
  automation: AutomationStatus;
  runs: AutomationHistoryRun[];
  historySource: 'pgboss' | 'workflow' | 'none';
}

/**
 * Runs the history reads: the API's cap. The row's "failing since" and the
 * modal both read this one query, so they always describe the same streak.
 */
export const HISTORY_LIMIT = 100;

export async function getAutomationHistory(automationId: string): Promise<AutomationHistoryResponse> {
  return apiClient.get(`/automations/${automationId}/history?limit=${HISTORY_LIMIT}`);
}

export interface TriggerAutomationResponse {
  success: boolean;
  jobId?: string | null;
  automationId: string;
  pgBossName?: string;
  alreadyRunning?: boolean;
  message?: string;
  syncRunId?: string;
  startedAt?: string;
}

export async function triggerAutomation(automationId: string): Promise<TriggerAutomationResponse> {
  return apiClient.post(`/automations/${automationId}/trigger`);
}

export interface StopAutomationResponse {
  success: boolean;
  automationId: string;
  cancelledJobIds: string[];
  totalCancelled: number;
}

export async function stopAutomation(automationId: string): Promise<StopAutomationResponse> {
  return apiClient.post(`/automations/${automationId}/stop`);
}

export interface AutomationProgressStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  detail?: string;
}

export interface AutomationProgressEntry {
  message: string;
  startedAt: string;
  updatedAt: string;
  steps?: AutomationProgressStep[];
}

export interface AutomationsProgressResponse {
  progress: Record<string, AutomationProgressEntry>;
}

export async function getAutomationsProgress(): Promise<AutomationsProgressResponse> {
  return apiClient.get('/automations/progress');
}
